import CharacterCount from "../ui/CharacterCount";
import React, { useEffect, useState } from "react";
import format from "date-fns/format";
import isToday from "date-fns/isToday";
import Button from "../ui/Button";
import PortalDrawer from "./PortalDrawer";
import { routeFromDealLegs } from "../../utilities/commercialTaskList";

const routeFromQuoteHeader = (header) => {
    const legs = header?.legs;
    if (!Array.isArray(legs) || !legs.length) return [];
    const points = [];
    legs.forEach((leg, i) => {
        const from = leg.departureAirport?.shortName;
        const to = leg.arrivalAirport?.shortName;
        if (i === 0 && from) points.push(from);
        if (to) points.push(to);
    });
    return points;
};

const formatNoteTime = (iso) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";
    const time = format(date, "HH:mm");
    return isToday(date) ? `Today, ${time}` : `${format(date, "d MMM")}, ${time}`;
};

const PortalQuoteFeedbackDrawer = ({
    open,
    onClose,
    deal,
    quoteHeader,
    notes = [],
    loading = false,
    canAdd = false,
    onAdd,
    canResolve = false,
    onResolve,
}) => {
    const [text, setText] = useState("");
    const [saving, setSaving] = useState(false);
    const [resolvingId, setResolvingId] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setText("");
            setError("");
        }
    }, [open]);

    if (!deal && !quoteHeader) return null;

    const dealRoute = routeFromDealLegs(deal);
    const route = dealRoute.length ? dealRoute : routeFromQuoteHeader(quoteHeader);
    const sortedNotes = [...notes].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    const handleSubmit = async () => {
        if (!text.trim() || !onAdd || saving) return;
        setSaving(true);
        setError("");
        try {
            await onAdd(text.trim());
            setText("");
        } catch {
            setError("Feedback could not be saved. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleResolve = async (id) => {
        if (!onResolve || resolvingId) return;
        setResolvingId(id);
        setError("");
        try {
            await onResolve(id);
        } catch {
            setError("Could not mark the feedback resolved. Please try again.");
        } finally {
            setResolvingId(null);
        }
    };

    return (
        <PortalDrawer
            open={open}
            onClose={onClose}
            title="Feedback"
            route={route}
            tripId={deal?.avinodeTripId}
            error={error}
            footer={
                canAdd && (
                    <div className="task-drawer__compose">
                        <label className="task-drawer__field">
                            <span id="feedback-field-label">Feedback</span>
                            <textarea
                                maxLength={2000}
                                aria-describedby="feedback-character-count"
                                aria-labelledby="feedback-field-label"
                                rows={3}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                placeholder="Leave a note..."
                            />
                            <CharacterCount id="feedback-character-count" count={text.length} maxLength={2000} />
                        </label>
                    </div>
                )
            }
            actions={
                <>
                    <Button classNames={["button button--secondary"]} onClick={onClose}>
                        {canAdd ? "Cancel" : "Close"}
                    </Button>
                    {canAdd && (
                        <Button
                            classNames={["button button--primary"]}
                            onClick={handleSubmit}
                            disabled={!text.trim() || saving}
                        >
                            {saving ? "Saving…" : "Add Feedback"}
                        </Button>
                    )}
                </>
            }
        >
            {sortedNotes.length > 0 ? (
                <div className="task-drawer__thread">
                    {sortedNotes.map((note) => (
                        <article
                            key={note.id}
                            className={`task-drawer__note${note.resolved ? " task-drawer__note--resolved" : ""}`}
                        >
                            <div className="task-drawer__note-head">
                                <div className="task-drawer__note-meta">
                                    <strong>{note.author}</strong>
                                    <span>{formatNoteTime(note.createdAt)}</span>
                                </div>
                                {note.resolved ? (
                                    <span className="task-drawer__note-status">Resolved</span>
                                ) : (
                                    canResolve && (
                                        <button
                                            type="button"
                                            className="task-drawer__resolve"
                                            onClick={() => handleResolve(note.id)}
                                            disabled={!!resolvingId}
                                        >
                                            {resolvingId === note.id ? "Resolving…" : "Mark resolved"}
                                        </button>
                                    )
                                )}
                            </div>
                            <p className="task-drawer__note-text">{note.text}</p>
                        </article>
                    ))}
                </div>
            ) : (
                <p className="task-drawer__empty">{loading ? "Loading feedback…" : "No feedback yet."}</p>
            )}
        </PortalDrawer>
    );
};

export default PortalQuoteFeedbackDrawer;
